import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  FaUser, 
  FaEnvelope, 
  FaPhone, 
  FaBriefcase 
} from 'react-icons/fa';
import { JOB_POSITIONS, VALIDATION_PATTERNS } from '../constants';
import { FormField, StatusMessage } from './common';
import Seo from './common/Seo';

const initialFormData = {
  name: '',
  email: '',
  phone: '',
  position: ''
};

function JobApplicationPage() {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ type: null, message: '' });
  const [formTouched, setFormTouched] = useState(false);
  const formRef = useRef(null);

  // Función de validación memoizada
  const validateField = useCallback((name, value) => {
    let error = '';

    switch (name) {
      case 'name':
        if (!value.trim()) {
          error = 'Nombre completo requerido';
        } else if (!VALIDATION_PATTERNS.name.test(value.trim())) {
          error = 'Por favor ingresa nombre y apellido válidos';
        }
        break;
      case 'email':
        if (!VALIDATION_PATTERNS.email.test(value)) error = 'Ingresa un correo electrónico válido';
        break;
      case 'phone':
        if (!VALIDATION_PATTERNS.phone.test(value)) error = 'Ingresa un número de 10 dígitos';
        break;
      case 'position':
        if (!value) error = 'Por favor selecciona un puesto';
        break;
      default:
        if (!value) error = 'Este campo es requerido';
    }
    
    return error;
  }, []);

  // Validación en tiempo real
  useEffect(() => {
    if (formTouched) {
      const newErrors = {};
      Object.keys(formData).forEach((field) => {
        newErrors[field] = validateField(field, formData[field]);
      });
      setErrors(newErrors);
    }
  }, [formData, formTouched, validateField]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Variable para almacenar el valor procesado
    let processedValue = value;
    
    // Validación en tiempo real para el teléfono (solo números)
    if (name === 'phone') {
      // Permitir solo números y limitar a 10 caracteres
      processedValue = value.replace(/\D/g, '').slice(0, 10);
    }
    
    setFormData(prev => ({ ...prev, [name]: processedValue }));
    
    if (errors[name]) {
      setErrors(prev => ({ 
        ...prev, 
        [name]: validateField(name, processedValue) 
      }));
    }
    
    setFormTouched(true);
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    
    Object.keys(formData).forEach((field) => {
      newErrors[field] = validateField(field, formData[field]);
      if (newErrors[field]) isValid = false;
    });
    
    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const isValid = validateForm();
    if (!isValid) {
      // Desplazar al primer error
      const firstErrorKey = Object.keys(errors).find(key => errors[key]);
      if (firstErrorKey) {
        const element = document.getElementById(firstErrorKey);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      return;
    }

    try {
      setStatus({ type: 'loading', message: 'Enviando solicitud...' });

      // Enviar formulario usando Formspree
      const response = await fetch('https://formspree.io/f/xkgbgagj', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          position: formData.position,
          _subject: 'Nueva solicitud de trabajo'
        })
      });

      if (response.ok) {
        setStatus({ type: 'success', message: '¡Solicitud enviada con éxito!' });
        setFormData(initialFormData);
        setErrors({});
        if (formRef.current) formRef.current.reset();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al enviar el formulario');
      }
    } catch (error) {
      console.error('Error:', error);
      setStatus({
        type: 'error',
        message: 'Error al enviar la solicitud. Por favor intenta más tarde.'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 pt-28 pb-14 px-4 sm:px-6 lg:px-8">
      <Seo
        title="Trabaja con Nosotros | Geriatría Serena"
        description="Únete al equipo de enfermeras, auxiliares y cuidadoras de Geriatría Serena en Guadalajara. Envía tu solicitud de empleo y forma parte de un equipo certificado."
        canonical="https://geriatriaserena.com/careers"
        meta={[
          { property: 'og:title', content: 'Trabaja con Nosotros | Geriatría Serena' },
          { property: 'og:description', content: 'Vacantes para enfermeras, auxiliares y cuidadoras en Guadalajara. Postúlate en línea.' },
          { property: 'og:type', content: 'website' },
          { property: 'og:url', content: 'https://geriatriaserena.com/careers' }
        ]}
      />
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-blue-700 py-6 px-8 relative">
          <div className="absolute top-4 right-4 bg-white bg-opacity-20 rounded-full p-2">
            <FaBriefcase className="text-white text-xl" />
          </div>
          <h1 className="text-3xl font-bold text-white text-center">
            Únete a Nuestro Equipo
          </h1>
          <p className="text-blue-200 text-center mt-2">
            Completa el formulario y nos pondremos en contacto contigo
          </p>
        </div>
        
        <div className="p-8">
          <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
            <FormField
              label="Nombre Completo"
              id="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              error={errors.name}
              required
              autoComplete="name"
              icon={FaUser}
            />

            <FormField
              label="Correo Electrónico"
              id="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={errors.email}
              required
              autoComplete="email"
              icon={FaEnvelope}
            />

            <FormField
              label="Teléfono"
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              error={errors.phone}
              required
              autoComplete="tel"
              icon={FaPhone}
            />

            <FormField
              label="Puesto Deseado"
              id="position"
              type="select"
              value={formData.position}
              onChange={handleChange}
              options={JOB_POSITIONS}
              error={errors.position}
              required
              autoComplete="off"
              icon={FaBriefcase}
            />

            <button
              type="submit"
              disabled={status.type === 'loading'}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 px-6 rounded-lg font-bold
              hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all
              flex items-center justify-center gap-2 text-lg shadow-md hover:shadow-lg transform hover:-translate-y-0.5
              duration-300 ease-in-out"
            >
              {status.type === 'loading' ? 'Enviando...' : 'Enviar Solicitud'}
            </button>

            <StatusMessage 
              type={status.type} 
              message={status.message} 
              className="mt-4"
            />
            
            <div className="text-center text-sm text-gray-500 mt-6">
              <p>Tu información está segura con nosotros. No compartiremos tus datos con terceros.</p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default JobApplicationPage;